import makeStyles from '@mui/styles/makeStyles'
import { paidConsumerDesktopNavHeight, paidConsumerMobileNavHeight } from '../../components/app-toolbar-paid-consumer/style'

const drawerWidth = 240

export const useStyles = makeStyles(({ spacing, palette, typography, breakpoints, transitions }) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'stretch',
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto',
    minHeight: `calc(100vh - ${paidConsumerMobileNavHeight}px)`,
    [breakpoints.up('lg')]: {
      minHeight: `calc(100vh - ${paidConsumerDesktopNavHeight}px)`
    },
    // Below `md` the sections inset themselves (see the flat* rules), so the
    // page drops its own gutter and the dividers between them run edge to edge.
    [breakpoints.down('md')]: {
      paddingLeft: 0,
      paddingRight: 0
    }
  },
  profileSectionDivider: {
    backgroundColor: palette.primary.main,
    height: '2px'
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    paddingTop: 0,
    paddingBottom: spacing(3),
    paddingLeft: 0,
    paddingRight: 0,
    // The drawer only renders from `md`; below that the column is the page.
    [breakpoints.up('md')]: {
      paddingLeft: spacing(4)
    },
    position: 'relative'
  },
  seniorityLevelContainer: {
    padding: 12
  },
  profileSectionTitle: {
    ...typography.body.l,
    fontWeight: 500
  },
  accordion: {
    boxShadow: 'none',
    borderTop: '1px solid #DDDBE0'
  },
  accordionDetails: {
    overflowX: 'auto',
    whiteSpace: 'nowrap'
  },
  accordionPrimaryIcon: {
    backgroundColor: palette.primary.main,
    borderRadius: '50%',
    color: '#FFF'
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    alignSelf: 'flex-start',
    // `top` is set inline from the measured app bar (see useAppBarOffset).
    position: 'sticky',
    backgroundColor: palette.primary.shade?.[5] || palette.primary.light,
    borderLeft: `4px solid ${palette.primary.main}`,
    borderTopRightRadius: spacing(2),
    borderBottomRightRadius: spacing(2),
    display: 'flex',
    flexDirection: 'column',
    paddingTop: spacing(3),
    paddingBottom: spacing(3),
    paddingLeft: spacing(2),
    paddingRight: spacing(2)
  },
  drawerHeading: {
    paddingLeft: spacing(2),
    paddingRight: spacing(2),
    paddingBottom: spacing(1.5),
    color: palette.primary.main,
    fontWeight: 700,
    letterSpacing: '0.1em',
    fontSize: 12,
    textTransform: 'uppercase'
  },
  nav: {
    padding: 0,
    width: '100%'
  },
  navItem: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: spacing(0.5),
    paddingBottom: spacing(0.5)
  },
  navLink: {
    width: '100%',
    display: 'block',
    boxSizing: 'border-box',
    paddingTop: spacing(1.5),
    paddingBottom: spacing(1.5),
    paddingLeft: spacing(2.5),
    paddingRight: spacing(2.5),
    borderRadius: 10,
    color: palette.grey.shade?.[100] || palette.text.primary,
    textDecoration: 'none',
    fontSize: 15,
    lineHeight: 1.5,
    border: '1px solid transparent',
    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.6)'
    },
    '&.active': {
      backgroundColor: palette.common.white,
      color: palette.primary.main,
      fontWeight: typography.fontWeightBold,
      borderColor: palette.primary.main
    }
  },
  // The mobile page has no Container gutter, so the header takes the same inset
  // as the sections and the titles line up. A faint band behind it, edge to
  // edge, separates the title from the first section now that nothing else
  // frames the content.
  header: {
    [breakpoints.down('md')]: {
      paddingLeft: spacing(3),
      paddingRight: spacing(3),
      backgroundColor: palette.grey.shade[5],
      borderBottom: `1px solid ${palette.divider}`
    }
  },
  /*
   * The mobile sections, drawn straight on the page by ProfileCard when the
   * layout provides `flat` (components/profile-card.js). Headings and gutter
   * content share the inset the self-padded rows (personal details, newsletter,
   * billing) already use, so everything lines up without a card edge.
   */
  sectionInset: {
    paddingLeft: spacing(3),
    paddingRight: spacing(3)
  },
  flatHeading: {
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
    paddingTop: spacing(3),
    paddingBottom: spacing(2)
  },
  // Above self-padded rows, which bring their own top spacing.
  flatHeadingTight: {
    paddingBottom: 0
  },
  flatToggleHeading: {
    margin: 0
  },
  // The disclosure button of a folded section: a full-width row, the heading's
  // own type, a tap-sized height.
  flatToggle: {
    width: '100%',
    minHeight: spacing(7),
    paddingTop: spacing(2),
    paddingBottom: spacing(2),
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing(2),
    textAlign: 'left',
    font: 'inherit',
    letterSpacing: 'inherit',
    transition: transitions.create('background-color', { duration: transitions.duration.shortest }),
    '&:hover': {
      backgroundColor: palette.action.hover
    },
    '&.Mui-focusVisible': {
      outline: `2px solid ${palette.primary.main}`,
      outlineOffset: -2
    }
  },
  flatToggleIcon: {
    flexShrink: 0,
    color: palette.action.active,
    transition: transitions.create('transform', { duration: transitions.duration.shorter })
  },
  flatToggleIconExpanded: {
    transform: 'rotate(180deg)'
  },
  flatBody: {
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
    paddingBottom: spacing(3)
  },
  pageTitle: {
    paddingTop: spacing(4),
    fontWeight: typography.fontWeightBold,
    color: palette.grey.shade[100]
  },
  pageSubtitle: {
    paddingBottom: spacing(3),
    paddingTop: spacing(1),
    color: palette.grey.shade[80] || palette.text.secondary
  },
  topicChip: {
    margin: spacing(0.5),
    borderRadius: spacing(1)
  },
  errorText: {
    color: 'red'
  },
  table: {
    display: 'block',
    overflowX: 'auto',
    whiteSpace: 'nowrap'
  }
}))
